'use client';

import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

export interface DeferredMountProps {
  children: ReactNode;
  fallback?: ReactNode;
  rootMargin?: string;
  /**
   * After this delay, mount children even if still offscreen so their fetches
   * warm in the background. Avoids scroll → mount → fetch feeling slow.
   * Set 0 to only mount when near the viewport.
   */
  warmAfterMs?: number;
}

export const DeferredMount = ({
  children,
  fallback = null,
  rootMargin = '480px',
  warmAfterMs = 1500,
}: DeferredMountProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isWarmed, setIsWarmed] = useState(false);
  const entry = useIntersectionObserver(containerRef as RefObject<Element>, {
    freezeOnceVisible: true,
    rootMargin,
  });
  const isNearViewport = Boolean(entry?.isIntersecting);
  const shouldMount = isNearViewport || isWarmed;

  useEffect(() => {
    if (warmAfterMs <= 0 || shouldMount) return;

    let timeoutId: number | undefined;
    let idleId: number | undefined;

    const warm = () => setIsWarmed(true);

    const scheduleWarm = () => {
      timeoutId = window.setTimeout(warm, warmAfterMs);
    };

    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(scheduleWarm, { timeout: warmAfterMs + 500 });
    } else {
      scheduleWarm();
    }

    return () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      if (idleId !== undefined && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [shouldMount, warmAfterMs]);

  return <div ref={containerRef}>{shouldMount ? children : fallback}</div>;
};
